import { ChangeDetectionStrategy, Component, contentChild, ElementRef, inject, input, TemplateRef, viewChildren } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { NgTemplateOutlet } from '@angular/common'
import { AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion'
import { BaseSubCoverageFormComponent } from '../base-sub-coverage/base-sub-coverage-form.component'
import { SubCoveragePanel } from '../../../models/coverages/coverage-panels.model'
import { PolicyStatusType } from '../../../models/policy/policy.model'
import { DetailviewNavigationService, registerNavPanels } from '@services/detailview-navigation.service'

/**
 * Renders one accordion panel per sub-coverage. Must sit inside the coverage form's `p-accordion`.
 * A projected `<ng-template let-panel>` is rendered below each panel's sub-coverage form.
 *
 * @example
 * <albert-sub-coverage-panels [activePanels]="activePanels()" [form]="form()" [isCreateMode]="isCreateMode()"
 *                             [mainIndex]="mainIndex()" [panels]="panels" />
 */
@Component({
  selector: 'albert-sub-coverage-panels',
  standalone: true,
  imports: [AccordionPanel, AccordionHeader, AccordionContent, BaseSubCoverageFormComponent, NgTemplateOutlet],
  templateUrl: './sub-coverage-panels.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubCoveragePanelsComponent {
  /** Sub-coverage panels to render, in display order */
  public readonly panels = input.required<readonly SubCoveragePanel[]>()
  /** The coverage form; each panel's key names one of its sub-coverage groups. */
  public readonly form = input.required<FormGroup>()
  /** Accordion values of the open panels; a panel's content is only rendered while it is open */
  public readonly activePanels = input.required<string[]>()
  /** Position of the coverage in the detailview's sections, used for the nav indexes */
  public readonly mainIndex = input<number>()
  /** Whether the insured is being created, passed on to the sub-coverage forms */
  public readonly isCreateMode = input.required<boolean>()
  /** External id of the insured, passed on to the sub-coverage forms */
  public readonly memberExternalId = input<string | null | undefined>()
  /** Status of the policy, passed on to the sub-coverage forms */
  public readonly policyStatus = input<PolicyStatusType | null | undefined>()

  /** Optional projected template rendered below each panel's sub-coverage form, with the panel as context */
  protected readonly panelExtra = contentChild<TemplateRef<{ $implicit: SubCoveragePanel }>>(TemplateRef)
  protected readonly nav = inject(DetailviewNavigationService)

  private readonly subPanels = viewChildren<ElementRef<HTMLElement>>('subPanel')

  constructor() {
    registerNavPanels(this.nav, this.subPanels)
  }

  /** Group of the coverage form that holds the panel's sub-coverage. */
  protected subCoverageForm(panel: SubCoveragePanel): FormGroup {
    return this.form().get(panel.key) as FormGroup
  }
}
